import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Upload, X, Sparkles, Loader2, ArrowDownUp } from "lucide-react";
import { Button } from "../ui/button";
import api from "../../lib/api";
import { compressImage } from "../../lib/imageCompression";
import { IMAGE_LABELS } from "../../lib/listingConstants";

const IMAGE_LABELS_SET = new Set(IMAGE_LABELS);

export default function ImageGridSection({ form, setForm, actionsRef }) {
    const { t } = useTranslation();
    const [uploading, setUploading] = useState(false);
    const [videoUploading, setVideoUploading] = useState(false);
    const [dragHover, setDragHover] = useState(false);  // Iter 28 — Drag&Drop visual state
    const dragIndexRef = useRef(null);

    const update = (key, value) => setForm((p) => ({ ...p, [key]: value }));

    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        await processUploadFiles(files);
        // input zurücksetzen, damit derselbe File erneut ausgewählt werden kann
        if (e?.target) e.target.value = "";
    };

    // Iter 28 — Gemeinsamer Upload-Pfad für File-Input UND Drag&Drop
    const processUploadFiles = async (rawFiles) => {
        const files = (rawFiles || []).filter((f) => f && f.type && f.type.startsWith("image/"));
        if (!files.length) return;
        setUploading(true);
        let totalSaved = 0;
        try {
            const uploaded = [];
            for (const raw of files) {
                // Phase 4.6 — auto-compress photos before upload
                // eslint-disable-next-line no-await-in-loop
                const f = await compressImage(raw);
                totalSaved += Math.max(0, raw.size - f.size);
                const fd = new FormData();
                fd.append("file", f);
                // eslint-disable-next-line no-await-in-loop
                const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                uploaded.push(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
            }
            const prevSnapshot = {
                images: form.images || [],
                labels: form.image_labels || [],
            };
            const newImages = [...prevSnapshot.images, ...uploaded];
            const newLabels = [...prevSnapshot.labels, ...uploaded.map(() => "")];
            setForm((p) => ({ ...p, images: newImages, image_labels: newLabels }));

            const savedMb = totalSaved / (1024 * 1024);
            if (savedMb >= 1) {
                toast.success(`${t("create_listing.toast_uploaded", { count: uploaded.length })} · ${savedMb.toFixed(1)} MB durch Komprimierung gespart`);
            } else {
                toast.success(t("create_listing.toast_uploaded", { count: uploaded.length }));
            }
            if (uploaded.length > 0) {
                const preSortSnapshot = { images: newImages.slice(), labels: newLabels.slice() };
                doAutoPosition(newImages, newLabels, {
                    silent: false,
                    auto: true,
                    undoSnapshot: preSortSnapshot,
                });
            }
        } catch {
            toast.error(t("create_listing.toast_upload_failed"));
        } finally {
            setUploading(false);
        }
    };

    // Iter 28 — Drag & Drop Handler
    const handleGridDragOver = (e) => {
        // Verhindert Default-Behavior und zeigt Drop-Effekt an
        if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes("Files")) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        if (!dragHover) setDragHover(true);
    };
    const handleGridDragLeave = (e) => {
        // Nur loslassen, wenn wir wirklich das Grid verlassen (nicht beim Hover über Children)
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setDragHover(false);
    };
    const handleGridDrop = async (e) => {
        e.preventDefault();
        setDragHover(false);
        const files = Array.from(e.dataTransfer?.files || []);
        await processUploadFiles(files);
    };

    const removeImage = (i) => {
        setForm((p) => ({
            ...p,
            images: (p.images || []).filter((_, idx) => idx !== i),
            image_labels: (p.image_labels || []).filter((_, idx) => idx !== i),
        }));
    };

    const setImageLabel = (i, label) => {
        setForm((p) => {
            const labels = [...(p.image_labels || [])];
            while (labels.length < (p.images || []).length) labels.push("");
            labels[i] = label;
            return { ...p, image_labels: labels };
        });
    };

    const moveImage = (fromIdx, toIdx) => {
        if (fromIdx === toIdx || toIdx < 0 || toIdx >= form.images.length) return;
        const imgs = [...form.images];
        const labels = [...(form.image_labels || [])];
        while (labels.length < imgs.length) labels.push("");
        const [movedImg] = imgs.splice(fromIdx, 1);
        const [movedLbl] = labels.splice(fromIdx, 1);
        imgs.splice(toIdx, 0, movedImg);
        labels.splice(toIdx, 0, movedLbl);
        setForm((p) => ({ ...p, images: imgs, image_labels: labels }));
    };

    const handleVideoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 100 * 1024 * 1024) {
            toast.error("Video zu groß (max. 100 MB).");
            return;
        }
        setVideoUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            update("video_url", `${process.env.REACT_APP_BACKEND_URL}${data.url}`);
            toast.success("Video hochgeladen");
        } catch {
            toast.error("Video-Upload fehlgeschlagen");
        } finally {
            setVideoUploading(false);
        }
    };

    // Phase 4.7 — AI-Analyse per Foto: Gemini Vision schlägt Label & Schäden vor
    const [analyzingIdx, setAnalyzingIdx] = useState(null);
    const [bulkProgress, setBulkProgress] = useState(null); // {done, total, stopped}
    const [aiSuggestions, setAiSuggestions] = useState({}); // idx → {damages, confidence}
    // Jul-2026 (User-Wunsch) — KI-Titelbild-Varianten (nur Desktop-Upload).
    const [titleBusy, setTitleBusy] = useState(false);
    const [titleVariants, setTitleVariants] = useState(null); // null | [{style,label,url}]

    const _relUrl = (fullUrl) => {
        const backend = process.env.REACT_APP_BACKEND_URL;
        return backend && fullUrl?.startsWith(backend) ? fullUrl.slice(backend.length) : fullUrl;
    };
    const generateTitleVariants = async () => {
        const src = (form.images || [])[0];
        if (!src) { toast.error("Bitte zuerst ein Foto hochladen."); return; }
        setTitleBusy(true);
        try {
            const { data } = await api.post("/listings/ai-title-image", {
                image_url: _relUrl(src),
                styles: ["studio_white", "gradient", "showroom"],
            });
            if (data?.variants?.length) setTitleVariants(data.variants);
            else toast.error("Keine KI-Varianten erhalten.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "KI-Titelbild fehlgeschlagen.");
        } finally { setTitleBusy(false); }
    };
    const pickTitleVariant = (url) => {
        const full = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
        setForm((p) => {
            const imgs = Array.isArray(p.images) ? p.images : [];
            const labels = Array.isArray(p.image_labels) ? [...p.image_labels] : [];
            while (labels.length < imgs.length) labels.push("");
            return { ...p, images: [full, ...imgs], image_labels: ["TITELBILD (KI)", ...labels] };
        });
        setTitleVariants(null);
        toast.success("KI-Titelbild als erstes Bild gesetzt.");
    };

    // Feb-2026 Iter 28: Reine URL-basierte Analyse — kein Closure auf form.images,
    // damit auch frisch hochgeladene Bilder (vor State-Flush) analysiert werden können.
    const analyzeUrl = async (fullUrl) => {
        if (!fullUrl) return { ok: false };
        const backend = process.env.REACT_APP_BACKEND_URL;
        const relUrl = backend && fullUrl.startsWith(backend) ? fullUrl.slice(backend.length) : fullUrl;
        try {
            const { data } = await api.post("/listings/ai-image-analyze", { image_url: relUrl });
            if (data?.suggested_label && IMAGE_LABELS_SET.has(data.suggested_label)) {
                return {
                    ok: true,
                    label: data.suggested_label,
                    damages: data.detected_damages || [],
                    confidence: data.confidence || 0,
                    photo_quality: data.photo_quality || 0,
                    photo_quality_tip: data.photo_quality_tip || "",
                    mocked: !!data.mocked,
                };
            }
            return {
                ok: false,
                mocked: !!data?.mocked,
                suggestion: data?.suggested_label || "",
                photo_quality: data?.photo_quality || 0,
                photo_quality_tip: data?.photo_quality_tip || "",
            };
        } catch {
            return { ok: false, error: true };
        }
    };

    const analyzeSingle = async (idx) => {
        const res = await analyzeUrl(form.images[idx]);
        if (res.ok) {
            setImageLabel(idx, res.label);
            setAiSuggestions((p) => ({
                ...p,
                [idx]: {
                    damages: res.damages,
                    confidence: res.confidence,
                    photo_quality: res.photo_quality || 0,
                    photo_quality_tip: res.photo_quality_tip || "",
                },
            }));
        } else if (res.photo_quality) {
            // Auch ohne Label-Match: Quality-Score speichern, damit ★1-5 sichtbar bleibt
            setAiSuggestions((p) => ({
                ...p,
                [idx]: {
                    ...(p[idx] || {}),
                    photo_quality: res.photo_quality,
                    photo_quality_tip: res.photo_quality_tip || "",
                },
            }));
        }
        return res;
    };

    const aiAnalyzeImage = async (idx) => {
        setAnalyzingIdx(idx);
        const res = await analyzeSingle(idx);
        setAnalyzingIdx(null);
        if (res.ok) {
            const dmgPart = res.damages.length ? ` · Schäden erkannt: ${res.damages.join(", ")}` : "";
            toast.success(`KI-Vorschlag übernommen: „${res.label}"${dmgPart}`);
        } else if (res.suggestion) {
            toast.info(`KI-Vorschlag „${res.suggestion}" passt nicht zum Vokabular — bitte manuell wählen`);
        } else if (res.mocked) {
            toast.info("KI-Analyse aktuell nicht verfügbar — bitte Label manuell wählen");
        } else if (res.error) {
            toast.error("KI-Analyse fehlgeschlagen");
        }
    };

    const aiAnalyzeAll = async () => {
        const images = form.images || [];
        if (!images.length) {
            toast.error("Noch keine Bilder zum Analysieren vorhanden");
            return;
        }
        // Nur unlabelled Bilder analysieren — schont Budget bei Re-Edits
        const targets = images
            .map((_, i) => i)
            .filter((i) => !(form.image_labels || [])[i]);
        if (!targets.length) {
            toast.info("Alle Bilder haben bereits ein Label — KI-Bulk-Analyse übersprungen");
            return;
        }
        let labeled = 0;
        let mocked_count = 0;
        for (let n = 0; n < targets.length; n += 1) {
            const idx = targets[n];
            setBulkProgress({ done: n, total: targets.length, stopped: false });
            // eslint-disable-next-line no-await-in-loop
            const res = await analyzeSingle(idx);
            if (res.ok) labeled += 1;
            if (res.mocked) mocked_count += 1;
            // Stopp-Bedingung: 3 Mock-Fallbacks in Folge = Budget oder Service-Down
            if (mocked_count >= 3) {
                setBulkProgress({ done: n + 1, total: targets.length, stopped: true });
                toast.warning("Bulk gestoppt: KI antwortet aktuell nicht (vermutlich Budget) — manuelle Labels nötig");
                return;
            }
        }
        setBulkProgress({ done: targets.length, total: targets.length, stopped: false });
        toast.success(`Bulk-Analyse fertig: ${labeled}/${targets.length} Bilder automatisch beschriftet`);
        setTimeout(() => setBulkProgress(null), 4000);
    };

    // Feb-2026 (Iter 28, User-Wunsch) — KI-Auto-Position: analysiert unlabelled
    // Bilder via Gemini Vision UND sortiert sie nach der kanonischen Reihenfolge
    // aus IMAGE_LABELS (Linke Seite → Rechte Seite → Vorne → Hinten → Cockpit …).
    // Manuell gewählte Labels bleiben erhalten. Unlabelled Bilder rutschen ans Ende.
    //
    // doAutoPosition akzeptiert EXPLIZITE images/labels-Arrays — damit funktioniert
    // der Aufruf auch unmittelbar nach setForm() (Closure-Problem umgangen).
    // opts.auto=true → Auto-Trigger nach Upload (Toast mit „Rückgängig"-Action)
    // opts.undoSnapshot → Pre-Sort-Zustand, der via Toast-Action wiederhergestellt wird
    const doAutoPosition = async (imagesArg, labelsArg, opts = {}) => {
        const images = imagesArg || [];
        const labels = (labelsArg || []).slice();
        while (labels.length < images.length) labels.push("");
        if (!images.length) {
            if (!opts.silent) toast.error("Noch keine Bilder zum Sortieren vorhanden");
            return;
        }
        // Schritt 1 — alle unbeschrifteten Bilder über die Vision-API kategorisieren
        const targetIndices = images.map((_, i) => i).filter((i) => !labels[i]);
        let mocked_count = 0;
        let analyzed = 0;
        const newLabels = labels.slice();
        const newSuggestions = {};
        for (let n = 0; n < targetIndices.length; n += 1) {
            const idx = targetIndices[n];
            setBulkProgress({ done: n, total: targetIndices.length, stopped: false });
            // eslint-disable-next-line no-await-in-loop
            const res = await analyzeUrl(images[idx]);
            if (res.ok) {
                newLabels[idx] = res.label;
                newSuggestions[idx] = {
                    damages: res.damages,
                    confidence: res.confidence,
                    photo_quality: res.photo_quality || 0,
                    photo_quality_tip: res.photo_quality_tip || "",
                };
                analyzed += 1;
            } else if (res.photo_quality) {
                newSuggestions[idx] = {
                    photo_quality: res.photo_quality,
                    photo_quality_tip: res.photo_quality_tip || "",
                };
            }
            if (res.mocked) mocked_count += 1;
            if (mocked_count >= 3) {
                setBulkProgress({ done: n + 1, total: targetIndices.length, stopped: true });
                toast.warning("KI-Analyse abgebrochen (Budget/Service) — Sortierung erfolgt nur mit vorhandenen Labels.");
                break;
            }
        }
        setBulkProgress({ done: targetIndices.length, total: targetIndices.length, stopped: false });

        // Schritt 2 — Bilder + Labels stabil nach IMAGE_LABELS-Reihenfolge sortieren.
        // Feb-2026 Iter 28 (Cover-Image-Selector): innerhalb einer Label-Gruppe wird
        // zusätzlich nach KI-Confidence DESC sortiert, damit z.B. das schärfste
        // „Linke Seite"-Foto auf Position 1 als Cover landet.
        const orderMap = new Map(IMAGE_LABELS.map((lbl, i) => [lbl, i]));
        const confidenceByOrigIdx = (origIdx) =>
            (newSuggestions[origIdx]?.confidence) ?? (aiSuggestions[origIdx]?.confidence) ?? 0;
        const pairs = images.map((src, origIdx) => ({
            src,
            label: newLabels[origIdx] || "",
            origIdx,
            confidence: confidenceByOrigIdx(origIdx),
        }));
        pairs.sort((a, b) => {
            const ai = orderMap.has(a.label) ? orderMap.get(a.label) : 999;
            const bi = orderMap.has(b.label) ? orderMap.get(b.label) : 999;
            if (ai !== bi) return ai - bi;
            // gleiche Label-Gruppe: höhere Confidence zuerst (Cover-Auswahl)
            if (a.confidence !== b.confidence) return b.confidence - a.confidence;
            return a.origIdx - b.origIdx; // stabiler Tie-Break
        });
        const sortedImages = pairs.map((x) => x.src);
        const sortedLabels = pairs.map((x) => x.label);

        setForm((p) => ({ ...p, images: sortedImages, image_labels: sortedLabels }));
        // Suggestions-Map muss nach Sort neu indiziert werden (origIdx → newIdx)
        setAiSuggestions((prev) => {
            const out = {};
            pairs.forEach((pair, newIdx) => {
                const src = pair.src;
                const fromAuto = newSuggestions[pair.origIdx];
                if (fromAuto) {
                    out[newIdx] = fromAuto;
                } else {
                    // Bestehende Suggestion (aus Single-Analyse) erhalten
                    const oldIdx = Object.entries(prev).find(([, v]) => v && v.__src === src)?.[0];
                    if (oldIdx != null && prev[oldIdx]) out[newIdx] = prev[oldIdx];
                }
            });
            return out;
        });

        const sortedCount = sortedLabels.filter((l) => l && orderMap.has(l)).length;
        if (!opts.silent) {
            const baseMsg = opts.auto
                ? `KI-Auto-Position: ${sortedCount} Bilder automatisch sortiert (Links → Rechts → Vorne → Hinten → …)`
                : `KI-Auto-Position fertig — ${sortedCount} Bilder nach Position sortiert (Links → Rechts → Vorne → Hinten → …)`;
            if (opts.auto && opts.undoSnapshot) {
                toast.success(baseMsg, {
                    duration: 8000,
                    action: {
                        label: "Rückgängig",
                        onClick: () => {
                            setForm((p) => ({
                                ...p,
                                images: opts.undoSnapshot.images.slice(),
                                image_labels: opts.undoSnapshot.labels.slice(),
                            }));
                            toast.info("Sortierung rückgängig gemacht — Original-Reihenfolge wiederhergestellt.");
                        },
                    },
                });
            } else if (analyzed === 0 && !opts.auto) {
                toast.info("Alle Bilder hatten bereits ein Label — nur sortiert.");
            } else {
                toast.success(baseMsg);
            }
        }
        setTimeout(() => setBulkProgress(null), 2500);
    };

    const autoPositionImages = () => doAutoPosition(form.images || [], form.image_labels || [], {});

    const showCockpitWarning =
        form.category === "motorcycle"
        && (form.images?.length || 0) > 0
        && !(form.image_labels || []).some((l) => l && l.toLowerCase().includes("cockpit"));

    // Iter 28 — Gesamt-AI-Refresh: erst Auto-Position (analysiert unlabelled + sortiert),
    // damit der Header-Button als „alle KI-Funktionen erneut anstoßen" wirkt.
    const aiRefreshAll = async () => {
        if (!(form.images || []).length) {
            toast.error("Noch keine Bilder zum Analysieren vorhanden");
            return;
        }
        // Labels leeren, damit doAutoPosition ALLE Bilder neu analysiert
        const imgsCopy = (form.images || []).slice();
        const labelsCleared = imgsCopy.map(() => "");
        setForm((p) => ({ ...p, image_labels: labelsCleared }));
        setAiSuggestions({});
        await doAutoPosition(imgsCopy, labelsCleared, { silent: false, auto: false });
    };

    const isProcessing = analyzingIdx !== null || (!!bulkProgress && bulkProgress.done < bulkProgress.total);

    // Feb-2026 Iter 28 (User-Wunsch 25 May, Update v4): Externes Trigger-Event
    // damit der KI-Button im SectionToggle-Header (außerhalb dieser Komponente)
    // aiRefreshAll anstoßen kann, ohne dass die Funktion via Ref gehoben werden muss.
    useEffect(() => {
        const handler = () => { aiRefreshAll(); };
        window.addEventListener("tw:images-ai-refresh-all", handler);
        return () => window.removeEventListener("tw:images-ai-refresh-all", handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.images, form.image_labels]);

    return (
        <>
            <BilddbSuggestions form={form} setForm={setForm} />
            {titleVariants && (
                <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="ai-title-variants-modal">
                    <div className="bg-white rounded-sm max-w-4xl w-full p-5 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-display text-lg font-bold flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#FFCC00]" /> KI-Titelbild wählen</h3>
                            <button type="button" onClick={() => setTitleVariants(null)} className="w-8 h-8 rounded-sm hover:bg-gray-100 flex items-center justify-center" data-testid="ai-title-variants-close"><X className="w-5 h-5" /></button>
                        </div>
                        <p className="text-xs text-gray-500 mb-4">Wählen Sie eine Variante — sie wird als erstes Inserat-Bild (Titelbild) gesetzt.</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {titleVariants.map((v) => (
                                <button key={v.style} type="button" onClick={() => pickTitleVariant(v.url)} className="group border-2 border-gray-200 hover:border-[#FFCC00] rounded-sm overflow-hidden text-left" data-testid={`ai-title-variant-${v.style}`}>
                                    <div className="aspect-video bg-gray-100">
                                        <img src={v.url.startsWith("http") ? v.url : `${process.env.REACT_APP_BACKEND_URL}${v.url}`} alt={v.label} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="px-2 py-2 flex items-center justify-between gap-2">
                                        <span className="text-xs font-bold text-[#111827]">{v.label}</span>
                                        <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FFCC00] text-[#111827] px-1.5 py-0.5 rounded-sm opacity-0 group-hover:opacity-100">Übernehmen</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {/* Images */}
            <div className="bg-white border border-gray-200 rounded-sm p-6">
                <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                    <h2 className="font-display text-lg font-bold">{t("create_listing.images_title")}</h2>
                    {(form.images || []).length > 0 && (
                        <Button
                            type="button"
                            onClick={aiRefreshAll}
                            disabled={isProcessing}
                            variant="outline"
                            className="border-2 border-[#111827] text-[#111827] hover:bg-[#FFCC00]/30 font-bold rounded-sm uppercase tracking-wider text-xs h-9 px-4"
                            data-testid="ai-refresh-header-btn"
                            title="Stößt alle KI-Foto-Funktionen erneut an: Labels, Schäden, Qualitäts-Score und Auto-Position."
                        >
                            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                            KI neu starten
                        </Button>
                    )}
                </div>

                {/* Iter 28 — KI-Processing-Banner: deutlich sichtbar während Vision-Calls */}
                {isProcessing && (
                    <div
                        className="mb-4 flex items-center gap-3 bg-[#FFFBEB] border-l-4 border-[#FFCC00] px-4 py-3 rounded-sm"
                        data-testid="ai-processing-banner"
                        role="status"
                        aria-live="polite"
                    >
                        <Loader2 className="w-5 h-5 text-[#111827] animate-spin shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-[#111827]">KI analysiert Ihre Bilder…</div>
                            <div className="text-xs text-gray-700 mt-0.5">
                                {bulkProgress
                                    ? `Bild ${bulkProgress.done + (bulkProgress.done < bulkProgress.total ? 1 : 0)} von ${bulkProgress.total} · Labels, Schäden und Qualitäts-Score werden ermittelt.`
                                    : `Bild ${(analyzingIdx ?? 0) + 1} wird gerade ausgewertet — bitte einen Moment Geduld.`}
                            </div>
                        </div>
                    </div>
                )}
                {showCockpitWarning && (
                    <div className="mb-4 bg-amber-50 border-l-4 border-amber-400 px-4 py-3 rounded-sm flex items-start gap-2" data-testid="cockpit-warning-banner">
                        <span className="text-amber-700 font-bold text-lg leading-none">!</span>
                        <div className="text-xs text-amber-900">
                            <strong>Empfohlen:</strong> Mindestens ein Bild sollte mit „Cockpit mit Tachostand" beschriftet sein. Käufer vertrauen Inseraten mit Tacho-Foto deutlich mehr.
                        </div>
                    </div>
                )}
                {/* Phase 4.8 — Bulk KI-Analyse */}
                {(form.images || []).length > 0 && (
                    <div className="mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="ai-bulk-toolbar">
                        <div className="text-xs text-gray-600">
                            <strong>{(form.images || []).length}</strong> Bilder · <strong>{(form.image_labels || []).filter(Boolean).length}</strong> mit Label
                        </div>
                        <div className="flex items-center gap-3">
                            {bulkProgress && (
                                <div className="flex items-center gap-2" data-testid="ai-bulk-progress">
                                    <div className="w-32 h-1.5 bg-gray-200 rounded-sm overflow-hidden">
                                        <div
                                            className={`h-full transition-all ${bulkProgress.stopped ? "bg-amber-500" : "bg-[#FFCC00]"}`}
                                            style={{ width: `${Math.round((bulkProgress.done / Math.max(1, bulkProgress.total)) * 100)}%` }}
                                        />
                                    </div>
                                    <span className="text-[11px] font-mono-num text-gray-600">
                                        {bulkProgress.done}/{bulkProgress.total}
                                    </span>
                                </div>
                            )}
                            <Button
                                type="button"
                                onClick={aiAnalyzeAll}
                                disabled={!!bulkProgress && bulkProgress.done < bulkProgress.total}
                                className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider text-xs h-9 px-4"
                                data-testid="ai-analyze-all-btn"
                            >
                                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                                Alle Fotos analysieren
                            </Button>
                            <Button
                                type="button"
                                onClick={autoPositionImages}
                                disabled={!!bulkProgress && bulkProgress.done < bulkProgress.total}
                                variant="outline"
                                className="border-2 border-[#111827] text-[#111827] hover:bg-[#FFCC00]/30 font-bold rounded-sm uppercase tracking-wider text-xs h-9 px-4"
                                data-testid="ai-auto-position-btn"
                                title="Analysiert unbeschriftete Fotos via KI und sortiert anschließend ALLE Bilder nach Position: Linke Seite → Rechte Seite → Vorne → Hinten → Cockpit → …"
                            >
                                <ArrowDownUp className="w-3.5 h-3.5 mr-1.5" />
                                KI-Auto-Position
                            </Button>
                            <Button
                                type="button"
                                onClick={generateTitleVariants}
                                disabled={titleBusy}
                                className="bg-[#FFCC00] hover:bg-[#e6b800] text-[#111827] font-bold rounded-sm uppercase tracking-wider text-xs h-9 px-4"
                                data-testid="ai-title-variants-btn"
                                title="Erzeugt aus dem ersten Foto 3 professionelle Titelbild-Varianten (weißer Studio-Hintergrund, dezenter Farbverlauf, Showroom) zur Auswahl."
                            >
                                {titleBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                                KI-Titelbild
                            </Button>
                        </div>
                    </div>
                )}
                <div
                    className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-4 rounded-sm transition-all ${dragHover ? "ring-4 ring-[#FFCC00] ring-offset-2 bg-[#FFFBEB]" : ""}`}
                    data-testid="images-grid"
                    onDragOver={handleGridDragOver}
                    onDragLeave={handleGridDragLeave}
                    onDrop={handleGridDrop}
                >
                    {(form.images || []).map((src, i) => (
                        <div
                            key={src}
                            className="bg-gray-50 border border-gray-200 rounded-sm overflow-hidden"
                            data-testid={`image-card-${i}`}
                            draggable
                            onDragStart={() => { dragIndexRef.current = i; }}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                                e.preventDefault();
                                const from = dragIndexRef.current;
                                dragIndexRef.current = null;
                                if (from != null) moveImage(from, i);
                            }}
                        >
                            <div className="relative aspect-square bg-gray-100">
                                <img src={src} alt="" className="w-full h-full object-cover pointer-events-none" />
                                <div className="absolute top-1 left-1 inline-flex items-center justify-center min-w-[26px] h-6 px-1.5 bg-[#FFCC00] text-[#111827] text-xs font-black font-mono-num rounded-sm" data-testid={`image-position-${i}`}>
                                    {i + 1}
                                </div>
                                <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 w-7 h-7 bg-black/70 text-white rounded-sm flex items-center justify-center hover:bg-black" data-testid={`image-remove-${i}`}>
                                    <X className="w-4 h-4" />
                                </button>
                                <div className="absolute bottom-1 right-1 flex gap-0.5">
                                    <button type="button" disabled={i === 0} onClick={() => moveImage(i, i - 1)} className="w-6 h-6 bg-white/90 hover:bg-white text-[#111827] rounded-sm flex items-center justify-center text-xs font-bold disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`image-move-up-${i}`} aria-label="Nach vorne verschieben">↑</button>
                                    <button type="button" disabled={i === form.images.length - 1} onClick={() => moveImage(i, i + 1)} className="w-6 h-6 bg-white/90 hover:bg-white text-[#111827] rounded-sm flex items-center justify-center text-xs font-bold disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`image-move-down-${i}`} aria-label="Nach hinten verschieben">↓</button>
                                </div>
                                {/* Feb-2026 Iter 28 (D) — Foto-Qualitäts-Score ★1-5 */}
                                {aiSuggestions[i]?.photo_quality > 0 && (
                                    <div
                                        className="absolute bottom-1 left-1 inline-flex items-center gap-0.5 px-1.5 h-6 bg-black/75 backdrop-blur text-[#FFCC00] text-[10px] font-mono-num rounded-sm tracking-wider"
                                        data-testid={`image-quality-${i}`}
                                        title={aiSuggestions[i].photo_quality_tip || `Foto-Qualität: ${aiSuggestions[i].photo_quality}/5`}
                                    >
                                        <span className="font-black">{aiSuggestions[i].photo_quality}</span>
                                        <span className="text-white/90">/5</span>
                                        <span className="ml-0.5 text-[#FFCC00]" aria-hidden>★</span>
                                    </div>
                                )}
                            </div>
                            <select
                                value={(form.image_labels || [])[i] || ""}
                                onChange={(e) => setImageLabel(i, e.target.value)}
                                className="w-full text-[11px] border-t border-gray-200 px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#FFCC00]"
                                data-testid={`image-label-${i}`}
                            >
                                <option value="">{`${i + 1}. — Beschriftung wählen —`}</option>
                                {IMAGE_LABELS.map((lbl) => <option key={lbl} value={lbl}>{`${i + 1}. ${lbl}`}</option>)}
                            </select>
                            <button
                                type="button"
                                onClick={() => aiAnalyzeImage(i)}
                                disabled={analyzingIdx === i}
                                className="w-full text-[10.5px] font-bold uppercase tracking-wider border-t border-gray-200 px-2 py-1.5 bg-[#FFFBEB] text-[#111827] hover:bg-[#FFCC00] disabled:opacity-60 flex items-center justify-center gap-1.5"
                                data-testid={`image-ai-analyze-${i}`}
                                title="Gemini schlägt Beschriftung und Schäden vor"
                            >
                                {analyzingIdx === i
                                    ? <><Loader2 className="w-3 h-3 animate-spin" /> Analysiere…</>
                                    : <><Sparkles className="w-3 h-3" /> KI-Analyse</>}
                            </button>
                            {aiSuggestions[i]?.damages?.length > 0 && (
                                <div className="border-t border-amber-200 bg-amber-50 px-2 py-1.5 text-[10px] text-amber-900 space-y-0.5" data-testid={`image-ai-damages-${i}`}>
                                    <div className="font-bold uppercase tracking-wider text-[9px] text-amber-700">Erkannte Schäden</div>
                                    {aiSuggestions[i].damages.map((d) => (
                                        <div key={d} className="flex items-start gap-1">
                                            <span className="text-red-500 shrink-0">●</span>
                                            <span>{d}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {/* Feb-2026 Iter 28 (D) — Foto-Qualitäts-Tipp bei Score ≤ 3 */}
                            {aiSuggestions[i]?.photo_quality > 0
                              && aiSuggestions[i].photo_quality <= 3
                              && aiSuggestions[i].photo_quality_tip && (
                                <div className="border-t border-red-200 bg-red-50 px-2 py-1.5 text-[10px] text-red-900" data-testid={`image-quality-tip-${i}`}>
                                    <div className="font-bold uppercase tracking-wider text-[9px] text-red-700 mb-0.5">Foto-Tipp</div>
                                    <div className="leading-snug">{aiSuggestions[i].photo_quality_tip}</div>
                                </div>
                            )}
                        </div>
                    ))}
                    <label className="aspect-square border-2 border-dashed border-gray-300 hover:border-[#111827] rounded-sm flex flex-col items-center justify-center cursor-pointer transition-colors">
                        <Upload className="w-6 h-6 text-gray-400 mb-1" />
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-500 text-center px-2">{uploading ? t("create_listing.images_uploading") : t("create_listing.images_upload")}</span>
                        <span className="mt-1 text-[10px] text-gray-400">Position {(form.images?.length || 0) + 1}</span>
                        <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" data-testid="image-upload-input" />
                    </label>
                </div>
                <p className="text-xs text-gray-500">{t("create_listing.images_hint")} Tipp: Per <strong>Drag &amp; Drop</strong> auf das Feld oben können mehrere Bilder gleichzeitig hochgeladen werden. Innerhalb des Rasters kannst du Bilder per Drag &amp; Drop oder ↑/↓ neu anordnen — die Nummer entspricht der Sortierung im Inserat.</p>
            </div>

            {/* Video-Upload (optional) */}
            <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="video-upload-section">
                <h2 className="font-display text-lg font-bold mb-1">Video (optional)</h2>
                <p className="text-xs text-gray-500 mb-3">Ein kurzer Video-Clip (max. 100 MB) bringt Ihr Inserat noch besser zur Geltung.</p>
                {form.video_url ? (
                    <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-sm p-3">
                        <video src={form.video_url} controls className="w-48 h-32 bg-black rounded-sm" data-testid="video-preview" />
                        <div className="flex-1 min-w-0">
                            <div className="text-xs text-gray-600 truncate">{form.video_url.split("/").pop()}</div>
                            <Button type="button" variant="outline" onClick={() => update("video_url", "")} className="mt-2 rounded-sm h-8 text-xs" data-testid="video-remove">
                                <X className="w-3 h-3 mr-1" /> Video entfernen
                            </Button>
                        </div>
                    </div>
                ) : (
                    <label className="block border-2 border-dashed border-gray-300 hover:border-[#111827] rounded-sm p-6 text-center cursor-pointer transition-colors">
                        <Upload className="w-6 h-6 text-gray-400 mb-1 mx-auto" />
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-500">{videoUploading ? "Video wird hochgeladen…" : "Video auswählen"}</span>
                        <input type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" data-testid="video-upload-input" />
                    </label>
                )}
            </div>
        </>
    );
}

/**
 * Bilddatenbank Phase 2b (· 280) — zeigt im Inserat-Formular passende
 * Bibliotheks-Bilder (Code-/Modell-Match nach Marke + Modell + Zustand) als
 * anklickbare Vorschläge. Klick hängt das Bild ans Inserat an; Symbolfotos
 * werden für die öffentliche Kennzeichnung in ``symbol_image_urls`` vermerkt.
 */
function BilddbSuggestions({ form, setForm }) {
    const [items, setItems] = useState([]);
    const [info, setInfo] = useState(null);

    useEffect(() => {
        const brand = (form.brand || "").trim();
        const model = (form.model || "").trim();
        const condition = (form.condition || "").trim();
        if (!brand || !model || !condition) { setItems([]); setInfo(null); return; }
        let cancelled = false;
        const tid = setTimeout(async () => {
            try {
                const params = { brand, model, condition };
                // KI-Fallback: erstes vorhandenes Bild mitgeben, falls kein Code-Treffer.
                const firstImg = (form.images || []).find(Boolean);
                if (firstImg) params.image_url = firstImg;
                const r = await api.get("/dealer/vehicle-images/match", { params });
                if (cancelled) return;
                setItems(r.data?.suggestions || []);
                setInfo({ count: r.data?.count || 0, shared: r.data?.shared_pool, via: r.data?.via });
            } catch { if (!cancelled) { setItems([]); setInfo(null); } }
        }, 400);
        return () => { cancelled = true; clearTimeout(tid); };
    }, [form.brand, form.model, form.condition]);

    const addImage = (entry) => {
        setForm((p) => {
            const existing = Array.isArray(p.images) ? p.images : [];
            const toAdd = (entry.image_urls || []).filter((u) => u && !existing.includes(u));
            if (!toAdd.length) { toast.info("Bilder bereits hinzugefügt"); return p; }
            const labels = [...(p.image_labels || [])];
            while (labels.length < existing.length) labels.push("");
            toAdd.forEach(() => labels.push(entry.is_orientation_only ? "Symbolfoto" : ""));
            const sym = entry.is_orientation_only
                ? Array.from(new Set([...(p.symbol_image_urls || []), ...toAdd]))
                : (p.symbol_image_urls || []);
            toast.success(`${toAdd.length} Bild(er) hinzugefügt`);
            return { ...p, images: [...existing, ...toAdd], image_labels: labels, symbol_image_urls: sym };
        });
    };

    if (!items.length) return null;
    return (
        <div className="bg-white border border-[#FFCC00] rounded-sm p-4 mb-4" data-testid="bilddb-form-suggestions">
            <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[#FFCC00]" />
                <span className="text-sm font-black text-[#111827]">Bilddatenbank-Vorschläge{info?.via === "ai" ? " (KI)" : ""}</span>
                <span className="text-[11px] text-gray-500">({info?.count} Treffer für {form.brand} {form.model})</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {items.map((e) => (
                    <button key={e.id} type="button" onClick={() => addImage(e)} className="group relative border border-gray-200 rounded-sm overflow-hidden hover:border-[#FFCC00] text-left" data-testid={`bilddb-suggestion-${e.id}`}>
                        <div className="aspect-video bg-gray-100">
                            {e.image_urls?.[0] && <img src={e.image_urls[0]} alt="" className="w-full h-full object-cover" />}
                        </div>
                        {e.is_orientation_only && <span className="absolute bottom-1 left-1 text-[9px] font-bold uppercase bg-black/70 text-white px-1 py-0.5 rounded-sm">Symbolfoto</span>}
                        <span className="absolute top-1 right-1 bg-[#FFCC00] text-[#111827] text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm opacity-0 group-hover:opacity-100">+ Hinzufügen</span>
                        <div className="px-2 py-1 text-[11px] font-bold text-[#111827] truncate">{e.model_name || e.model_code || "Bibliothek"}{e.image_urls?.length > 1 ? ` (${e.image_urls.length})` : ""}</div>
                    </button>
                ))}
            </div>
        </div>
    );
}
